import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  collection,
  query,
  where,
  getDocs,
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  FaEye,
  FaEyeSlash,
  FaHeadset,
  FaLock,
  FaUser,
  FaShieldAlt,
  FaGlobe,
} from 'react-icons/fa';
import './Login.css';

const languages = [
  { code: 'en', label: 'English', short: 'EN', flag: '🇬🇧' },
  { code: 'sw', label: 'Kiswahili', short: 'SW', flag: '🇹🇿' },
  { code: 'pt', label: 'Português', short: 'PT', flag: '🇵🇹' },
  { code: 'fr', label: 'Français', short: 'FR', flag: '🇫🇷' },
  { code: 'es', label: 'Español', short: 'ES', flag: '🇪🇸' },
  { code: 'pt-AO', label: 'Português (Angola)', short: 'AO', flag: '🇦🇴' },
  { code: 'ar', label: 'العربية', short: 'AR', flag: '🇸🇦' },
  { code: 'zh', label: '中文', short: 'ZH', flag: '🇨🇳' },
  { code: 'hi', label: 'हिन्दी', short: 'HI', flag: '🇮🇳' },
  { code: 'bn', label: 'বাংলা', short: 'BN', flag: '🇧🇩' },
  { code: 'ru', label: 'Русский', short: 'RU', flag: '🇷🇺' },
  { code: 'de', label: 'Deutsch', short: 'DE', flag: '🇩🇪' },
  { code: 'it', label: 'Italiano', short: 'IT', flag: '🇮🇹' },
  { code: 'nl', label: 'Nederlands', short: 'NL', flag: '🇳🇱' },
  { code: 'tr', label: 'Türkçe', short: 'TR', flag: '🇹🇷' },
  { code: 'ja', label: '日本語', short: 'JA', flag: '🇯🇵' },
  { code: 'ko', label: '한국어', short: 'KO', flag: '🇰🇷' },
  { code: 'vi', label: 'Tiếng Việt', short: 'VI', flag: '🇻🇳' },
  { code: 'th', label: 'ไทย', short: 'TH', flag: '🇹🇭' },
  { code: 'id', label: 'Bahasa Indonesia', short: 'ID', flag: '🇮🇩' },
  { code: 'ms', label: 'Bahasa Melayu', short: 'MS', flag: '🇲🇾' },
  { code: 'ur', label: 'اردو', short: 'UR', flag: '🇵🇰' },
  { code: 'fa', label: 'فارسی', short: 'FA', flag: '🇮🇷' },
  { code: 'ha', label: 'Hausa', short: 'HA', flag: '🇳🇬' },
  { code: 'yo', label: 'Yorùbá', short: 'YO', flag: '🇳🇬' },
  { code: 'zu', label: 'isiZulu', short: 'ZU', flag: '🇿🇦' },
  { code: 'am', label: 'አማርኛ', short: 'AM', flag: '🇪🇹' },
  { code: 'so', label: 'Soomaali', short: 'SO', flag: '🇸🇴' },
  { code: 'rw', label: 'Ikinyarwanda', short: 'RW', flag: '🇷🇼' },
  { code: 'lg', label: 'Luganda', short: 'LG', flag: '🇺🇬' },
  { code: 'af', label: 'Afrikaans', short: 'AF', flag: '🇿🇦' },

];

const Login = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [securityCode, setSecurityCode] = useState('');
  const [userCode, setUserCode] = useState('');
  const [emptyCodeAttempts, setEmptyCodeAttempts] = useState(0);
  const [blockedMessage, setBlockedMessage] = useState('');

  const [language, setLanguage] = useState(
    () => localStorage.getItem('loginLanguage') || 'en'
  );
  const [showLangDropdown, setShowLangDropdown] = useState(false);

  // WhatsApp Groups and Channels
  const [whatsAppItems, setWhatsAppItems] = useState([]);
  const [whatsAppLoading, setWhatsAppLoading] = useState(true);

  const navigate = useNavigate();

  const currentLanguage =
    languages.find((item) => item.code === language) || languages[0];

  const text = {
    en: {
      secureAccess: 'SECURE ACCESS',
      welcome: 'Welcome Back',
      subtitle: 'Sign in to your account',
      username: 'USERNAME',
      usernamePlaceholder: 'Enter username',
      password: 'PASSWORD',
      passwordPlaceholder: 'Enter password',
      verification: 'VERIFICATION CODE',
      verificationInstruction: 'Enter the code shown above',
      verificationPlaceholder: 'Enter verification code',
      login: 'LOGIN',
      loading: 'Signing in...',
      customerCare: 'Customer Care',
      needHelp: 'Need help?',
      noAccount: "Don't have an account?",
      register: 'Register here',
      language: 'Language',
      usernameRequired: 'Enter your username',
      passwordRequired: 'Enter your password',
      wrongCode: 'Verification code is incorrect',
      codeRequired: 'Enter the verification code.',
      codeAutoFilled:
        'Verification code filled automatically. Click LOGIN again to continue.',
      userNotFound: 'Invalid username',
      blockedTitle: 'Account Blocked',
      blockedMessage: 'Your account has been blocked. Contact Customer Care.',
      wrongPassword: 'Password is incorrect',
      networkError: 'Network error, please try again',
      joinGroup: 'Join Group',
      followChannel: 'Follow Channel',
    },

    sw: {
      secureAccess: 'SECURE ACCESS',
      welcome: 'Karibu Tena',
      subtitle: 'Ingia kwenye akaunti yako',
      username: 'USERNAME',
      usernamePlaceholder: 'Ingiza username',
      password: 'PASSWORD',
      passwordPlaceholder: 'Ingiza password',
      verification: 'VERIFICATION CODE',
      verificationInstruction: 'Ingiza code inayoonekana hapo juu',
      verificationPlaceholder: 'Ingiza verification code',
      login: 'INGIA',
      loading: 'Inaingia...',
      customerCare: 'Customer Care',
      needHelp: 'Unahitaji msaada?',
      noAccount: 'Huna akaunti?',
      register: 'Jisajili hapa',
      language: 'Lugha',
      usernameRequired: 'Ingiza username yako',
      passwordRequired: 'Ingiza password yako',
      wrongCode: 'Verification code si sahihi',
      codeRequired: 'Tafadhali ingiza verification code.',
      codeAutoFilled:
        'Verification code imejazwa yenyewe. Bonyeza INGIA tena ili kuendelea.',
      userNotFound: 'Username sio sahihi',
      blockedTitle: 'Akaunti Imefungwa',
      blockedMessage: 'Akaunti yako imefungwa. Wasiliana na Customer Care.',
      wrongPassword: 'Password sio sahihi',
      networkError: 'Hitilafu ya mtandao, jaribu tena',
      joinGroup: 'Join Group',
      followChannel: 'Follow Channel',
    },

    "pt": {
      secureAccess: "ACESSO SEGURO",
      welcome: "Bem-vindo de volta",
      subtitle: "Inicie sessão na sua conta",
      username: "NOME DE UTILIZADOR",
      usernamePlaceholder: "Introduza o nome de utilizador",
      password: "PALAVRA-PASSE",
      passwordPlaceholder: "Introduza a palavra-passe",
      verification: "CÓDIGO DE VERIFICAÇÃO",
      verificationInstruction: "Introduza o código apresentado acima",
      verificationPlaceholder: "Introduza o código de verificação",
      login: "ENTRAR",
      loading: "A iniciar sessão...",
      customerCare: "Apoio ao Cliente",
      needHelp: "Precisa de ajuda?",
      noAccount: "Não tem uma conta?",
      register: "Registe-se aqui",
      language: "Idioma",
      usernameRequired: "Introduza o seu nome de utilizador",
      passwordRequired: "Introduza a sua palavra-passe",
      wrongCode: "O código de verificação está incorreto",
      codeRequired: "Introduza o código de verificação.",
      codeAutoFilled: "O código foi preenchido automaticamente. Clique novamente em ENTRAR para continuar.",
      userNotFound: "Nome de utilizador inválido",
      blockedTitle: "Conta bloqueada",
      blockedMessage: "A sua conta foi bloqueada. Contacte o Apoio ao Cliente.",
      wrongPassword: "Palavra-passe incorreta",
      networkError: "Erro de rede. Tente novamente.",
      joinGroup: "Participar no grupo",
      followChannel: "Seguir o canal",
    },

    "fr": {
      secureAccess: "ACCÈS SÉCURISÉ",
      welcome: "Bon retour",
      subtitle: "Connectez-vous à votre compte",
      username: "NOM D'UTILISATEUR",
      usernamePlaceholder: "Saisissez votre nom d'utilisateur",
      password: "MOT DE PASSE",
      passwordPlaceholder: "Saisissez votre mot de passe",
      verification: "CODE DE VÉRIFICATION",
      verificationInstruction: "Saisissez le code affiché ci-dessus",
      verificationPlaceholder: "Saisissez le code de vérification",
      login: "SE CONNECTER",
      loading: "Connexion en cours...",
      customerCare: "Service client",
      needHelp: "Besoin d'aide ?",
      noAccount: "Vous n'avez pas de compte ?",
      register: "Inscrivez-vous ici",
      language: "Langue",
      usernameRequired: "Saisissez votre nom d'utilisateur",
      passwordRequired: "Saisissez votre mot de passe",
      wrongCode: "Le code de vérification est incorrect",
      codeRequired: "Veuillez saisir le code de vérification.",
      codeAutoFilled: "Le code a été rempli automatiquement. Cliquez à nouveau sur SE CONNECTER pour continuer.",
      userNotFound: "Nom d'utilisateur invalide",
      blockedTitle: "Compte bloqué",
      blockedMessage: "Votre compte a été bloqué. Contactez le service client.",
      wrongPassword: "Mot de passe incorrect",
      networkError: "Erreur réseau. Veuillez réessayer.",
      joinGroup: "Rejoindre le groupe",
      followChannel: "Suivre la chaîne",
    },

    "es": {
      secureAccess: "ACCESO SEGURO",
      welcome: "Bienvenido de nuevo",
      subtitle: "Inicia sesión en tu cuenta",
      username: "NOMBRE DE USUARIO",
      usernamePlaceholder: "Introduce tu nombre de usuario",
      password: "CONTRASEÑA",
      passwordPlaceholder: "Introduce tu contraseña",
      verification: "CÓDIGO DE VERIFICACIÓN",
      verificationInstruction: "Introduce el código que aparece arriba",
      verificationPlaceholder: "Introduce el código de verificación",
      login: "INICIAR SESIÓN",
      loading: "Iniciando sesión...",
      customerCare: "Atención al cliente",
      needHelp: "¿Necesitas ayuda?",
      noAccount: "¿No tienes una cuenta?",
      register: "Regístrate aquí",
      language: "Idioma",
      usernameRequired: "Introduce tu nombre de usuario",
      passwordRequired: "Introduce tu contraseña",
      wrongCode: "El código de verificación es incorrecto",
      codeRequired: "Introduce el código de verificación.",
      codeAutoFilled: "El código se ha completado automáticamente. Pulsa INICIAR SESIÓN de nuevo para continuar.",
      userNotFound: "Nombre de usuario no válido",
      blockedTitle: "Cuenta bloqueada",
      blockedMessage: "Tu cuenta ha sido bloqueada. Contacta con atención al cliente.",
      wrongPassword: "La contraseña es incorrecta",
      networkError: "Error de red. Inténtalo de nuevo.",
      joinGroup: "Unirse al grupo",
      followChannel: "Seguir el canal",
    },

    "pt-AO": {
      secureAccess: "ACESSO SEGURO",
      welcome: "Bem-vindo de novo",
      subtitle: "Inicie sessão na sua conta",
      username: "NOME DE UTILIZADOR",
      usernamePlaceholder: "Introduza o seu nome de utilizador",
      password: "PALAVRA-PASSE",
      passwordPlaceholder: "Introduza a sua palavra-passe",
      verification: "CÓDIGO DE VERIFICAÇÃO",
      verificationInstruction: "Introduza o código apresentado acima",
      verificationPlaceholder: "Introduza o código de verificação",
      login: "INICIAR SESSÃO",
      loading: "A iniciar sessão...",
      customerCare: "Apoio ao Cliente",
      needHelp: "Precisa de ajuda?",
      noAccount: "Ainda não tem uma conta?",
      register: "Registe-se aqui",
      language: "Idioma",
      usernameRequired: "Introduza o seu nome de utilizador",
      passwordRequired: "Introduza a sua palavra-passe",
      wrongCode: "O código de verificação está incorrecto",
      codeRequired: "Introduza o código de verificação.",
      codeAutoFilled: "O código foi preenchido automaticamente. Clique novamente em INICIAR SESSÃO para continuar.",
      userNotFound: "Nome de utilizador inválido",
      blockedTitle: "Conta bloqueada",
      blockedMessage: "A sua conta foi bloqueada. Contacte o Apoio ao Cliente.",
      wrongPassword: "A palavra-passe está incorrecta",
      networkError: "Erro de rede. Tente novamente.",
      joinGroup: "Entrar no grupo",
      followChannel: "Seguir o canal",
    },



    ar: {
      secureAccess: 'دخول آمن',
      welcome: 'مرحبًا بعودتك',
      subtitle: 'سجّل الدخول إلى حسابك',
      username: 'اسم المستخدم',
      usernamePlaceholder: 'أدخل اسم المستخدم',
      password: 'كلمة المرور',
      passwordPlaceholder: 'أدخل كلمة المرور',
      verification: 'رمز التحقق',
      verificationInstruction: 'أدخل الرمز الظاهر أعلاه',
      verificationPlaceholder: 'أدخل رمز التحقق',
      login: 'تسجيل الدخول',
      loading: 'جارٍ تسجيل الدخول...',
      customerCare: 'خدمة العملاء',
      needHelp: 'هل تحتاج إلى مساعدة؟',
      noAccount: 'ليس لديك حساب؟',
      register: 'سجّل هنا',
      language: 'اللغة',
      usernameRequired: 'أدخل اسم المستخدم',
      passwordRequired: 'أدخل كلمة المرور',
      wrongCode: 'رمز التحقق غير صحيح',
      codeRequired: 'أدخل رمز التحقق.',
      codeAutoFilled: 'تم ملء الرمز تلقائيًا. اضغط على تسجيل الدخول مرة أخرى للمتابعة.',
      userNotFound: 'اسم المستخدم غير صالح',
      blockedTitle: 'الحساب محظور',
      blockedMessage: 'تم حظر حسابك. اتصل بخدمة العملاء.',
      wrongPassword: 'كلمة المرور غير صحيحة',
      networkError: 'خطأ في الشبكة، يرجى المحاولة مرة أخرى',
      joinGroup: 'الانضمام إلى المجموعة',
      followChannel: 'متابعة القناة',
    },

    zh: {
      secureAccess: '安全登录',
      welcome: '欢迎回来',
      subtitle: '登录您的账户',
      username: '用户名',
      usernamePlaceholder: '请输入用户名',
      password: '密码',
      passwordPlaceholder: '请输入密码',
      verification: '验证码',
      verificationInstruction: '请输入上方显示的验证码',
      verificationPlaceholder: '请输入验证码',
      login: '登录',
      loading: '正在登录...',
      customerCare: '客户服务',
      needHelp: '需要帮助吗？',
      noAccount: '还没有账户？',
      register: '立即注册',
      language: '语言',
      usernameRequired: '请输入用户名',
      passwordRequired: '请输入密码',
      wrongCode: '验证码错误',
      codeRequired: '请输入验证码。',
      codeAutoFilled: '验证码已自动填写。请再次点击登录以继续。',
      userNotFound: '用户名无效',
      blockedTitle: '账户已被封锁',
      blockedMessage: '您的账户已被封锁。请联系客户服务。',
      wrongPassword: '密码错误',
      networkError: '网络错误，请重试',
      joinGroup: '加入群组',
      followChannel: '关注频道',
    },

    hi: {
      secureAccess: 'सुरक्षित प्रवेश',
      welcome: 'वापसी पर स्वागत है',
      subtitle: 'अपने खाते में लॉगिन करें',
      username: 'उपयोगकर्ता नाम',
      usernamePlaceholder: 'उपयोगकर्ता नाम दर्ज करें',
      password: 'पासवर्ड',
      passwordPlaceholder: 'पासवर्ड दर्ज करें',
      verification: 'सत्यापन कोड',
      verificationInstruction: 'ऊपर दिखाया गया कोड दर्ज करें',
      verificationPlaceholder: 'सत्यापन कोड दर्ज करें',
      login: 'लॉगिन',
      loading: 'लॉगिन हो रहा है...',
      customerCare: 'ग्राहक सहायता',
      needHelp: 'क्या आपको सहायता चाहिए?',
      noAccount: 'खाता नहीं है?',
      register: 'यहाँ पंजीकरण करें',
      language: 'भाषा',
      usernameRequired: 'उपयोगकर्ता नाम दर्ज करें',
      passwordRequired: 'पासवर्ड दर्ज करें',
      wrongCode: 'सत्यापन कोड गलत है',
      codeRequired: 'सत्यापन कोड दर्ज करें।',
      codeAutoFilled: 'कोड अपने आप भर दिया गया है। जारी रखने के लिए फिर से लॉगिन दबाएँ।',
      userNotFound: 'अमान्य उपयोगकर्ता नाम',
      blockedTitle: 'खाता अवरुद्ध',
      blockedMessage: 'आपका खाता अवरुद्ध कर दिया गया है। ग्राहक सहायता से संपर्क करें।',
      wrongPassword: 'पासवर्ड गलत है',
      networkError: 'नेटवर्क त्रुटि, कृपया पुनः प्रयास करें',
      joinGroup: 'ग्रुप में शामिल हों',
      followChannel: 'चैनल फ़ॉलो करें',
    },

    bn: {
      secureAccess: 'নিরাপদ প্রবেশ',
      welcome: 'আবারও স্বাগতম',
      subtitle: 'আপনার অ্যাকাউন্টে লগইন করুন',
      username: 'ব্যবহারকারীর নাম',
      usernamePlaceholder: 'ব্যবহারকারীর নাম লিখুন',
      password: 'পাসওয়ার্ড',
      passwordPlaceholder: 'পাসওয়ার্ড লিখুন',
      verification: 'যাচাইকরণ কোড',
      verificationInstruction: 'উপরে দেখানো কোডটি লিখুন',
      verificationPlaceholder: 'যাচাইকরণ কোড লিখুন',
      login: 'লগইন',
      loading: 'লগইন হচ্ছে...',
      customerCare: 'গ্রাহক সহায়তা',
      needHelp: 'সাহায্য প্রয়োজন?',
      noAccount: 'অ্যাকাউন্ট নেই?',
      register: 'এখানে নিবন্ধন করুন',
      language: 'ভাষা',
      usernameRequired: 'ব্যবহারকারীর নাম লিখুন',
      passwordRequired: 'পাসওয়ার্ড লিখুন',
      wrongCode: 'যাচাইকরণ কোডটি সঠিক নয়',
      codeRequired: 'যাচাইকরণ কোড লিখুন।',
      codeAutoFilled: 'কোডটি স্বয়ংক্রিয়ভাবে পূরণ হয়েছে। চালিয়ে যেতে আবার লগইনে ক্লিক করুন।',
      userNotFound: 'ব্যবহারকারীর নাম সঠিক নয়',
      blockedTitle: 'অ্যাকাউন্ট অবরুদ্ধ',
      blockedMessage: 'আপনার অ্যাকাউন্ট অবরুদ্ধ করা হয়েছে। গ্রাহক সহায়তায় যোগাযোগ করুন।',
      wrongPassword: 'পাসওয়ার্ড সঠিক নয়',
      networkError: 'নেটওয়ার্ক সমস্যা, আবার চেষ্টা করুন',
      joinGroup: 'গ্রুপে যোগ দিন',
      followChannel: 'চ্যানেল অনুসরণ করুন',
    },

    ru: {
      secureAccess: 'БЕЗОПАСНЫЙ ВХОД',
      welcome: 'С возвращением',
      subtitle: 'Войдите в свою учётную запись',
      username: 'ИМЯ ПОЛЬЗОВАТЕЛЯ',
      usernamePlaceholder: 'Введите имя пользователя',
      password: 'ПАРОЛЬ',
      passwordPlaceholder: 'Введите пароль',
      verification: 'КОД ПОДТВЕРЖДЕНИЯ',
      verificationInstruction: 'Введите код, показанный выше',
      verificationPlaceholder: 'Введите код подтверждения',
      login: 'ВОЙТИ',
      loading: 'Выполняется вход...',
      customerCare: 'Служба поддержки',
      needHelp: 'Нужна помощь?',
      noAccount: 'Нет аккаунта?',
      register: 'Зарегистрируйтесь здесь',
      language: 'Язык',
      usernameRequired: 'Введите имя пользователя',
      passwordRequired: 'Введите пароль',
      wrongCode: 'Неверный код подтверждения',
      codeRequired: 'Введите код подтверждения.',
      codeAutoFilled: 'Код заполнен автоматически. Нажмите «ВОЙТИ» ещё раз, чтобы продолжить.',
      userNotFound: 'Неверное имя пользователя',
      blockedTitle: 'Аккаунт заблокирован',
      blockedMessage: 'Ваш аккаунт заблокирован. Обратитесь в службу поддержки.',
      wrongPassword: 'Неверный пароль',
      networkError: 'Ошибка сети. Попробуйте ещё раз.',
      joinGroup: 'Вступить в группу',
      followChannel: 'Подписаться на канал',
    },

    de: {
      secureAccess: 'SICHERER ZUGRIFF',
      welcome: 'Willkommen zurück',
      subtitle: 'Melde dich bei deinem Konto an',
      username: 'BENUTZERNAME',
      usernamePlaceholder: 'Benutzernamen eingeben',
      password: 'PASSWORT',
      passwordPlaceholder: 'Passwort eingeben',
      verification: 'BESTÄTIGUNGSCODE',
      verificationInstruction: 'Gib den oben angezeigten Code ein',
      verificationPlaceholder: 'Bestätigungscode eingeben',
      login: 'ANMELDEN',
      loading: 'Anmeldung läuft...',
      customerCare: 'Kundenservice',
      needHelp: 'Brauchst du Hilfe?',
      noAccount: 'Du hast noch kein Konto?',
      register: 'Hier registrieren',
      language: 'Sprache',
      usernameRequired: 'Bitte Benutzernamen eingeben',
      passwordRequired: 'Bitte Passwort eingeben',
      wrongCode: 'Der Bestätigungscode ist falsch',
      codeRequired: 'Bitte Bestätigungscode eingeben.',
      codeAutoFilled: 'Der Code wurde automatisch ausgefüllt. Klicke erneut auf ANMELDEN, um fortzufahren.',
      userNotFound: 'Ungültiger Benutzername',
      blockedTitle: 'Konto gesperrt',
      blockedMessage: 'Dein Konto wurde gesperrt. Bitte kontaktiere den Kundenservice.',
      wrongPassword: 'Das Passwort ist falsch',
      networkError: 'Netzwerkfehler. Bitte versuche es erneut.',
      joinGroup: 'Gruppe beitreten',
      followChannel: 'Kanal folgen',
    },

    it: {
      secureAccess: 'ACCESSO SICURO',
      welcome: 'Bentornato',
      subtitle: 'Accedi al tuo account',
      username: 'NOME UTENTE',
      usernamePlaceholder: 'Inserisci il nome utente',
      password: 'PASSWORD',
      passwordPlaceholder: 'Inserisci la password',
      verification: 'CODICE DI VERIFICA',
      verificationInstruction: 'Inserisci il codice mostrato sopra',
      verificationPlaceholder: 'Inserisci il codice di verifica',
      login: 'ACCEDI',
      loading: 'Accesso in corso...',
      customerCare: 'Assistenza clienti',
      needHelp: 'Hai bisogno di aiuto?',
      noAccount: 'Non hai un account?',
      register: 'Registrati qui',
      language: 'Lingua',
      usernameRequired: 'Inserisci il nome utente',
      passwordRequired: 'Inserisci la password',
      wrongCode: 'Il codice di verifica non è corretto',
      codeRequired: 'Inserisci il codice di verifica.',
      codeAutoFilled: 'Il codice è stato compilato automaticamente. Premi di nuovo ACCEDI per continuare.',
      userNotFound: 'Nome utente non valido',
      blockedTitle: 'Account bloccato',
      blockedMessage: 'Il tuo account è stato bloccato. Contatta l’assistenza clienti.',
      wrongPassword: 'La password non è corretta',
      networkError: 'Errore di rete. Riprova.',
      joinGroup: 'Unisciti al gruppo',
      followChannel: 'Segui il canale',
    },

    nl: {
      secureAccess: 'VEILIGE TOEGANG',
      welcome: 'Welkom terug',
      subtitle: 'Log in op je account',
      username: 'GEBRUIKERSNAAM',
      usernamePlaceholder: 'Voer je gebruikersnaam in',
      password: 'WACHTWOORD',
      passwordPlaceholder: 'Voer je wachtwoord in',
      verification: 'VERIFICATIECODE',
      verificationInstruction: 'Voer de code hierboven in',
      verificationPlaceholder: 'Voer de verificatiecode in',
      login: 'INLOGGEN',
      loading: 'Bezig met inloggen...',
      customerCare: 'Klantenservice',
      needHelp: 'Hulp nodig?',
      noAccount: 'Heb je geen account?',
      register: 'Registreer hier',
      language: 'Taal',
      usernameRequired: 'Voer je gebruikersnaam in',
      passwordRequired: 'Voer je wachtwoord in',
      wrongCode: 'De verificatiecode is onjuist',
      codeRequired: 'Voer de verificatiecode in.',
      codeAutoFilled: 'De code is automatisch ingevuld. Klik opnieuw op INLOGGEN om door te gaan.',
      userNotFound: 'Ongeldige gebruikersnaam',
      blockedTitle: 'Account geblokkeerd',
      blockedMessage: 'Je account is geblokkeerd. Neem contact op met de klantenservice.',
      wrongPassword: 'Het wachtwoord is onjuist',
      networkError: 'Netwerkfout. Probeer het opnieuw.',
      joinGroup: 'Lid worden van de groep',
      followChannel: 'Kanaal volgen',
    },

    tr: {
      secureAccess: 'GÜVENLİ ERİŞİM',
      welcome: 'Tekrar Hoş Geldiniz',
      subtitle: 'Hesabınıza giriş yapın',
      username: 'KULLANICI ADI',
      usernamePlaceholder: 'Kullanıcı adını girin',
      password: 'ŞİFRE',
      passwordPlaceholder: 'Şifrenizi girin',
      verification: 'DOĞRULAMA KODU',
      verificationInstruction: 'Yukarıda gösterilen kodu girin',
      verificationPlaceholder: 'Doğrulama kodunu girin',
      login: 'GİRİŞ YAP',
      loading: 'Giriş yapılıyor...',
      customerCare: 'Müşteri Hizmetleri',
      needHelp: 'Yardıma mı ihtiyacınız var?',
      noAccount: 'Hesabınız yok mu?',
      register: 'Buradan kaydolun',
      language: 'Dil',
      usernameRequired: 'Kullanıcı adınızı girin',
      passwordRequired: 'Şifrenizi girin',
      wrongCode: 'Doğrulama kodu yanlış',
      codeRequired: 'Doğrulama kodunu girin.',
      codeAutoFilled: 'Kod otomatik dolduruldu. Devam etmek için tekrar GİRİŞ YAP düğmesine basın.',
      userNotFound: 'Geçersiz kullanıcı adı',
      blockedTitle: 'Hesap Engellendi',
      blockedMessage: 'Hesabınız engellendi. Müşteri hizmetleriyle iletişime geçin.',
      wrongPassword: 'Şifre yanlış',
      networkError: 'Ağ hatası, lütfen tekrar deneyin',
      joinGroup: 'Gruba Katıl',
      followChannel: 'Kanalı Takip Et',
    },

    ja: {
      secureAccess: '安全なアクセス',
      welcome: 'おかえりなさい',
      subtitle: 'アカウントにログインしてください',
      username: 'ユーザー名',
      usernamePlaceholder: 'ユーザー名を入力',
      password: 'パスワード',
      passwordPlaceholder: 'パスワードを入力',
      verification: '認証コード',
      verificationInstruction: '上に表示されたコードを入力してください',
      verificationPlaceholder: '認証コードを入力',
      login: 'ログイン',
      loading: 'ログイン中...',
      customerCare: 'カスタマーサポート',
      needHelp: 'お困りですか？',
      noAccount: 'アカウントをお持ちでないですか？',
      register: 'こちらから登録',
      language: '言語',
      usernameRequired: 'ユーザー名を入力してください',
      passwordRequired: 'パスワードを入力してください',
      wrongCode: '認証コードが正しくありません',
      codeRequired: '認証コードを入力してください。',
      codeAutoFilled: 'コードが自動入力されました。続行するにはもう一度ログインを押してください。',
      userNotFound: 'ユーザー名が無効です',
      blockedTitle: 'アカウントがブロックされています',
      blockedMessage: 'アカウントがブロックされています。カスタマーサポートにお問い合わせください。',
      wrongPassword: 'パスワードが正しくありません',
      networkError: 'ネットワークエラーです。もう一度お試しください。',
      joinGroup: 'グループに参加',
      followChannel: 'チャンネルをフォロー',
    },

    ko: {
      secureAccess: '보안 접속',
      welcome: '다시 오신 것을 환영합니다',
      subtitle: '계정에 로그인하세요',
      username: '사용자 이름',
      usernamePlaceholder: '사용자 이름 입력',
      password: '비밀번호',
      passwordPlaceholder: '비밀번호 입력',
      verification: '인증 코드',
      verificationInstruction: '위에 표시된 코드를 입력하세요',
      verificationPlaceholder: '인증 코드 입력',
      login: '로그인',
      loading: '로그인 중...',
      customerCare: '고객 지원',
      needHelp: '도움이 필요하신가요?',
      noAccount: '계정이 없으신가요?',
      register: '여기에서 가입',
      language: '언어',
      usernameRequired: '사용자 이름을 입력하세요',
      passwordRequired: '비밀번호를 입력하세요',
      wrongCode: '인증 코드가 올바르지 않습니다',
      codeRequired: '인증 코드를 입력하세요.',
      codeAutoFilled: '코드가 자동으로 입력되었습니다. 계속하려면 로그인 버튼을 다시 누르세요.',
      userNotFound: '유효하지 않은 사용자 이름',
      blockedTitle: '계정 차단됨',
      blockedMessage: '계정이 차단되었습니다. 고객 지원에 문의하세요.',
      wrongPassword: '비밀번호가 올바르지 않습니다',
      networkError: '네트워크 오류입니다. 다시 시도하세요.',
      joinGroup: '그룹 참여',
      followChannel: '채널 팔로우',
    },


    vi: {
      secureAccess: 'TRUY CẬP AN TOÀN',
      welcome: 'Chào mừng bạn trở lại',
      subtitle: 'Đăng nhập vào tài khoản của bạn',
      username: 'TÊN NGƯỜI DÙNG',
      usernamePlaceholder: 'Nhập tên người dùng',
      password: 'MẬT KHẨU',
      passwordPlaceholder: 'Nhập mật khẩu',
      verification: 'MÃ XÁC MINH',
      verificationInstruction: 'Nhập mã hiển thị ở trên',
      verificationPlaceholder: 'Nhập mã xác minh',
      login: 'ĐĂNG NHẬP',
      loading: 'Đang đăng nhập...',
      customerCare: 'Chăm sóc khách hàng',
      needHelp: 'Bạn cần trợ giúp?',
      noAccount: 'Bạn chưa có tài khoản?',
      register: 'Đăng ký tại đây',
      language: 'Ngôn ngữ',
      usernameRequired: 'Vui lòng nhập tên người dùng',
      passwordRequired: 'Vui lòng nhập mật khẩu',
      wrongCode: 'Mã xác minh không chính xác',
      codeRequired: 'Vui lòng nhập mã xác minh.',
      codeAutoFilled: 'Mã đã được điền tự động. Nhấn ĐĂNG NHẬP lần nữa để tiếp tục.',
      userNotFound: 'Tên người dùng không hợp lệ',
      blockedTitle: 'Tài khoản bị khóa',
      blockedMessage: 'Tài khoản của bạn đã bị khóa. Vui lòng liên hệ bộ phận chăm sóc khách hàng.',
      wrongPassword: 'Mật khẩu không chính xác',
      networkError: 'Lỗi mạng, vui lòng thử lại',
      joinGroup: 'Tham gia nhóm',
      followChannel: 'Theo dõi kênh',
    },

    th: {
      secureAccess: 'เข้าสู่ระบบอย่างปลอดภัย',
      welcome: 'ยินดีต้อนรับกลับมา',
      subtitle: 'เข้าสู่ระบบบัญชีของคุณ',
      username: 'ชื่อผู้ใช้',
      usernamePlaceholder: 'กรอกชื่อผู้ใช้',
      password: 'รหัสผ่าน',
      passwordPlaceholder: 'กรอกรหัสผ่าน',
      verification: 'รหัสยืนยัน',
      verificationInstruction: 'กรอกรหัสที่แสดงด้านบน',
      verificationPlaceholder: 'กรอกรหัสยืนยัน',
      login: 'เข้าสู่ระบบ',
      loading: 'กำลังเข้าสู่ระบบ...',
      customerCare: 'ฝ่ายบริการลูกค้า',
      needHelp: 'ต้องการความช่วยเหลือหรือไม่?',
      noAccount: 'ยังไม่มีบัญชีใช่ไหม?',
      register: 'สมัครที่นี่',
      language: 'ภาษา',
      usernameRequired: 'กรุณากรอกชื่อผู้ใช้',
      passwordRequired: 'กรุณากรอกรหัสผ่าน',
      wrongCode: 'รหัสยืนยันไม่ถูกต้อง',
      codeRequired: 'กรุณากรอกรหัสยืนยัน',
      codeAutoFilled: 'กรอกรหัสให้อัตโนมัติแล้ว คลิกเข้าสู่ระบบอีกครั้งเพื่อดำเนินการต่อ',
      userNotFound: 'ชื่อผู้ใช้ไม่ถูกต้อง',
      blockedTitle: 'บัญชีถูกระงับ',
      blockedMessage: 'บัญชีของคุณถูกระงับ กรุณาติดต่อฝ่ายบริการลูกค้า',
      wrongPassword: 'รหัสผ่านไม่ถูกต้อง',
      networkError: 'เกิดข้อผิดพลาดของเครือข่าย โปรดลองอีกครั้ง',
      joinGroup: 'เข้าร่วมกลุ่ม',
      followChannel: 'ติดตามช่อง',
    },

    id: {
      secureAccess: 'AKSES AMAN',
      welcome: 'Selamat Datang Kembali',
      subtitle: 'Masuk ke akun Anda',
      username: 'NAMA PENGGUNA',
      usernamePlaceholder: 'Masukkan nama pengguna',
      password: 'KATA SANDI',
      passwordPlaceholder: 'Masukkan kata sandi',
      verification: 'KODE VERIFIKASI',
      verificationInstruction: 'Masukkan kode yang ditampilkan di atas',
      verificationPlaceholder: 'Masukkan kode verifikasi',
      login: 'MASUK',
      loading: 'Sedang masuk...',
      customerCare: 'Layanan Pelanggan',
      needHelp: 'Butuh bantuan?',
      noAccount: 'Belum punya akun?',
      register: 'Daftar di sini',
      language: 'Bahasa',
      usernameRequired: 'Masukkan nama pengguna Anda',
      passwordRequired: 'Masukkan kata sandi Anda',
      wrongCode: 'Kode verifikasi salah',
      codeRequired: 'Masukkan kode verifikasi.',
      codeAutoFilled: 'Kode terisi otomatis. Klik MASUK lagi untuk melanjutkan.',
      userNotFound: 'Nama pengguna tidak valid',
      blockedTitle: 'Akun Diblokir',
      blockedMessage: 'Akun Anda telah diblokir. Hubungi layanan pelanggan.',
      wrongPassword: 'Kata sandi salah',
      networkError: 'Kesalahan jaringan, silakan coba lagi',
      joinGroup: 'Gabung Grup',
      followChannel: 'Ikuti Saluran',
    },

    ms: {
      secureAccess: 'AKSES SELAMAT',
      welcome: 'Selamat Kembali',
      subtitle: 'Log masuk ke akaun anda',
      username: 'NAMA PENGGUNA',
      usernamePlaceholder: 'Masukkan nama pengguna',
      password: 'KATA LALUAN',
      passwordPlaceholder: 'Masukkan kata laluan',
      verification: 'KOD PENGESAHAN',
      verificationInstruction: 'Masukkan kod yang dipaparkan di atas',
      verificationPlaceholder: 'Masukkan kod pengesahan',
      login: 'LOG MASUK',
      loading: 'Sedang log masuk...',
      customerCare: 'Khidmat Pelanggan',
      needHelp: 'Perlukan bantuan?',
      noAccount: 'Belum mempunyai akaun?',
      register: 'Daftar di sini',
      language: 'Bahasa',
      usernameRequired: 'Masukkan nama pengguna anda',
      passwordRequired: 'Masukkan kata laluan anda',
      wrongCode: 'Kod pengesahan tidak betul',
      codeRequired: 'Sila masukkan kod pengesahan.',
      codeAutoFilled: 'Kod diisi secara automatik. Klik LOG MASUK sekali lagi untuk meneruskan.',
      userNotFound: 'Nama pengguna tidak sah',
      blockedTitle: 'Akaun Disekat',
      blockedMessage: 'Akaun anda telah disekat. Hubungi khidmat pelanggan.',
      wrongPassword: 'Kata laluan tidak betul',
      networkError: 'Ralat rangkaian, sila cuba lagi',
      joinGroup: 'Sertai Kumpulan',
      followChannel: 'Ikuti Saluran',
    },

    ur: {
      secureAccess: 'محفوظ رسائی',
      welcome: 'واپس خوش آمدید',
      subtitle: 'اپنے اکاؤنٹ میں لاگ ان کریں',
      username: 'صارف نام',
      usernamePlaceholder: 'صارف نام درج کریں',
      password: 'پاس ورڈ',
      passwordPlaceholder: 'پاس ورڈ درج کریں',
      verification: 'تصدیقی کوڈ',
      verificationInstruction: 'اوپر دکھایا گیا کوڈ درج کریں',
      verificationPlaceholder: 'تصدیقی کوڈ درج کریں',
      login: 'لاگ ان',
      loading: 'لاگ ان ہو رہا ہے...',
      customerCare: 'کسٹمر سروس',
      needHelp: 'مدد چاہیے؟',
      noAccount: 'اکاؤنٹ نہیں ہے؟',
      register: 'یہاں رجسٹر کریں',
      language: 'زبان',
      usernameRequired: 'اپنا صارف نام درج کریں',
      passwordRequired: 'اپنا پاس ورڈ درج کریں',
      wrongCode: 'تصدیقی کوڈ غلط ہے',
      codeRequired: 'تصدیقی کوڈ درج کریں۔',
      codeAutoFilled: 'کوڈ خودکار طور پر درج ہو گیا ہے۔ جاری رکھنے کے لیے دوبارہ لاگ ان دبائیں۔',
      userNotFound: 'صارف نام درست نہیں',
      blockedTitle: 'اکاؤنٹ بلاک ہے',
      blockedMessage: 'آپ کا اکاؤنٹ بلاک کر دیا گیا ہے۔ کسٹمر سروس سے رابطہ کریں۔',
      wrongPassword: 'پاس ورڈ غلط ہے',
      networkError: 'نیٹ ورک کی خرابی، دوبارہ کوشش کریں',
      joinGroup: 'گروپ میں شامل ہوں',
      followChannel: 'چینل فالو کریں',
    },

    fa: {
      secureAccess: 'دسترسی امن',
      welcome: 'خوش آمدید',
      subtitle: 'وارد حساب کاربری خود شوید',
      username: 'نام کاربری',
      usernamePlaceholder: 'نام کاربری را وارد کنید',
      password: 'گذرواژه',
      passwordPlaceholder: 'گذرواژه را وارد کنید',
      verification: 'کد تأیید',
      verificationInstruction: 'کد نمایش‌داده‌شده در بالا را وارد کنید',
      verificationPlaceholder: 'کد تأیید را وارد کنید',
      login: 'ورود',
      loading: 'در حال ورود...',
      customerCare: 'پشتیبانی مشتریان',
      needHelp: 'به کمک نیاز دارید؟',
      noAccount: 'حساب کاربری ندارید؟',
      register: 'اینجا ثبت‌نام کنید',
      language: 'زبان',
      usernameRequired: 'نام کاربری را وارد کنید',
      passwordRequired: 'گذرواژه را وارد کنید',
      wrongCode: 'کد تأیید نادرست است',
      codeRequired: 'لطفاً کد تأیید را وارد کنید.',
      codeAutoFilled: 'کد به‌طور خودکار وارد شد. برای ادامه دوباره روی ورود کلیک کنید.',
      userNotFound: 'نام کاربری نامعتبر است',
      blockedTitle: 'حساب مسدود شده است',
      blockedMessage: 'حساب شما مسدود شده است. با پشتیبانی مشتریان تماس بگیرید.',
      wrongPassword: 'گذرواژه نادرست است',
      networkError: 'خطای شبکه، لطفاً دوباره تلاش کنید',
      joinGroup: 'پیوستن به گروه',
      followChannel: 'دنبال کردن کانال',
    },

    ha: {
      secureAccess: 'SHIGA CIKIN TSARO',
      welcome: 'Barka da dawowa',
      subtitle: 'Shiga cikin asusunka',
      username: 'SUNAN MAI AMFANI',
      usernamePlaceholder: 'Shigar da sunan mai amfani',
      password: 'KALMAR SIRRI',
      passwordPlaceholder: 'Shigar da kalmar sirri',
      verification: 'LAMBAN TABBATARWA',
      verificationInstruction: 'Shigar da lambar da aka nuna a sama',
      verificationPlaceholder: 'Shigar da lambar tabbatarwa',
      login: 'SHIGA',
      loading: 'Ana shiga...',
      customerCare: 'Taimakon Abokan Ciniki',
      needHelp: 'Kana buƙatar taimako?',
      noAccount: 'Ba ka da asusu?',
      register: 'Yi rajista a nan',
      language: 'Harshe',
      usernameRequired: 'Shigar da sunan mai amfani',
      passwordRequired: 'Shigar da kalmar sirri',
      wrongCode: 'Lambar tabbatarwa ba daidai ba ce',
      codeRequired: 'Shigar da lambar tabbatarwa.',
      codeAutoFilled: 'An cika lambar ta atomatik. Danna SHIGA kuma don ci gaba.',
      userNotFound: 'Sunan mai amfani ba daidai ba ne',
      blockedTitle: 'An Toshe Asusun',
      blockedMessage: 'An toshe asusunka. Tuntuɓi taimakon abokan ciniki.',
      wrongPassword: 'Kalmar sirri ba daidai ba ce',
      networkError: 'Kuskuren hanyar sadarwa, sake gwadawa',
      joinGroup: 'Shiga Ƙungiya',
      followChannel: 'Bi Tashar',
    },

    yo: {
      secureAccess: 'WỌLE AABO',
      welcome: 'Ẹ káàbọ̀ padà',
      subtitle: 'Wọlé sí àkọọ́lẹ̀ rẹ',
      username: 'ORÚKỌ OLÙLO',
      usernamePlaceholder: 'Tẹ orúkọ olùlo rẹ',
      password: 'Ọ̀RỌ̀ AṢÍRÍ',
      passwordPlaceholder: 'Tẹ ọ̀rọ̀ aṣírí rẹ',
      verification: 'KÓÓDÙ ÌMÚLẸ̀',
      verificationInstruction: 'Tẹ kóòdù tí a fi hàn lókè',
      verificationPlaceholder: 'Tẹ kóòdù ìmúlẹ̀',
      login: 'WỌLẸ̀',
      loading: 'Ó ń wọlé...',
      customerCare: 'Ìrànlọ́wọ́ Oníbàárà',
      needHelp: 'Ṣé o nílò ìrànlọ́wọ́?',
      noAccount: 'O kò ní àkọọ́lẹ̀?',
      register: ' forukọsilẹ nibi',
      language: 'Èdè',
      usernameRequired: 'Tẹ orúkọ olùlo rẹ',
      passwordRequired: 'Tẹ ọ̀rọ̀ aṣírí rẹ',
      wrongCode: 'Kóòdù ìmúlẹ̀ kò tọ́',
      codeRequired: 'Jọ̀wọ́ tẹ kóòdù ìmúlẹ̀.',
      codeAutoFilled: 'A ti tẹ kóòdù náà fúnra rẹ̀. Tẹ WỌLẸ̀ lẹ́ẹ̀kansi láti tẹ̀síwájú.',
      userNotFound: 'Orúkọ olùlo kò tọ́',
      blockedTitle: 'A Ti Dènà Àkọọ́lẹ̀',
      blockedMessage: 'A ti dènà àkọọ́lẹ̀ rẹ. Kàn sí ìrànlọ́wọ́ oníbàárà.',
      wrongPassword: 'Ọ̀rọ̀ aṣírí kò tọ́',
      networkError: 'Àṣìṣe nẹ́tíwọ́ọ̀kì, jọ̀wọ́ gbìyànjú lẹ́ẹ̀kansi',
      joinGroup: 'Darapọ̀ mọ́ Ẹgbẹ́',
      followChannel: 'Tẹ̀lé Ikanni',
    },

    zu: {
      secureAccess: 'UKUNGENA OKUPHEPHILE',
      welcome: 'Siyakwamukela futhi',
      subtitle: 'Ngena ku-akhawunti yakho',
      username: 'IGAMA LOMSEBENZISI',
      usernamePlaceholder: 'Faka igama lomsebenzisi',
      password: 'IPHASIWEDI',
      passwordPlaceholder: 'Faka iphasiwedi',
      verification: 'IKHODI YOKUQINISEKISA',
      verificationInstruction: 'Faka ikhodi eboniswe ngenhla',
      verificationPlaceholder: 'Faka ikhodi yokuqinisekisa',
      login: 'NGENA',
      loading: 'Kuyangena...',
      customerCare: 'Usizo Lwamakhasimende',
      needHelp: 'Udinga usizo?',
      noAccount: 'Awunayo i-akhawunti?',
      register: 'Bhalisa lapha',
      language: 'Ulimi',
      usernameRequired: 'Faka igama lakho lomsebenzisi',
      passwordRequired: 'Faka iphasiwedi yakho',
      wrongCode: 'Ikhodi yokuqinisekisa ayilungile',
      codeRequired: 'Faka ikhodi yokuqinisekisa.',
      codeAutoFilled: 'Ikhodi igcwaliswe ngokuzenzakalela. Chofoza u-NGENA futhi ukuze uqhubeke.',
      userNotFound: 'Igama lomsebenzisi alilungile',
      blockedTitle: 'I-akhawunti Ivinjiwe',
      blockedMessage: 'I-akhawunti yakho ivinjiwe. Xhumana nosizo lwamakhasimende.',
      wrongPassword: 'Iphasiwedi ayilungile',
      networkError: 'Iphutha lenethiwekhi, zama futhi',
      joinGroup: 'Joyina Iqembu',
      followChannel: 'Landela Isiteshi',
    },

    am: {
      secureAccess: 'ደህንነቱ የተጠበቀ መግቢያ',
      welcome: 'እንኳን በደህና ተመለሱ',
      subtitle: 'ወደ መለያዎ ይግቡ',
      username: 'የተጠቃሚ ስም',
      usernamePlaceholder: 'የተጠቃሚ ስም ያስገቡ',
      password: 'የይለፍ ቃል',
      passwordPlaceholder: 'የይለፍ ቃል ያስገቡ',
      verification: 'የማረጋገጫ ኮድ',
      verificationInstruction: 'ከላይ የሚታየውን ኮድ ያስገቡ',
      verificationPlaceholder: 'የማረጋገጫ ኮድ ያስገቡ',
      login: 'ግባ',
      loading: 'በመግባት ላይ...',
      customerCare: 'የደንበኛ ድጋፍ',
      needHelp: 'እርዳታ ይፈልጋሉ?',
      noAccount: 'መለያ የለዎትም?',
      register: 'እዚህ ይመዝገቡ',
      language: 'ቋንቋ',
      usernameRequired: 'የተጠቃሚ ስም ያስገቡ',
      passwordRequired: 'የይለፍ ቃል ያስገቡ',
      wrongCode: 'የማረጋገጫ ኮዱ ትክክል አይደለም',
      codeRequired: 'የማረጋገጫ ኮዱን ያስገቡ።',
      codeAutoFilled: 'ኮዱ በራስ-ሰር ተሞልቷል። ለመቀጠል እንደገና ግባን ይጫኑ።',
      userNotFound: 'ትክክል ያልሆነ የተጠቃሚ ስም',
      blockedTitle: 'መለያው ታግዷል',
      blockedMessage: 'መለያዎ ታግዷል። የደንበኛ ድጋፍን ያነጋግሩ።',
      wrongPassword: 'የይለፍ ቃሉ ትክክል አይደለም',
      networkError: 'የኔትወርክ ስህተት፣ እንደገና ይሞክሩ',
      joinGroup: 'ቡድኑን ይቀላቀሉ',
      followChannel: 'ቻናሉን ይከታተሉ',
    },

    so: {
      secureAccess: 'GALITAAN AMMAAN AH',
      welcome: 'Ku soo dhowow mar kale',
      subtitle: 'Gal akoonkaaga',
      username: 'MAGACA ISTICMAALAHA',
      usernamePlaceholder: 'Geli magaca isticmaalaha',
      password: 'ERAYGA SIRTA',
      passwordPlaceholder: 'Geli erayga sirta ah',
      verification: 'KOOODHKA XAQIIJINTA',
      verificationInstruction: 'Geli koodhka kor ku muuqda',
      verificationPlaceholder: 'Geli koodhka xaqiijinta',
      login: 'GAL',
      loading: 'Waa la galayaa...',
      customerCare: 'Adeegga Macaamiisha',
      needHelp: 'Ma u baahan tahay caawimo?',
      noAccount: 'Akoon ma lihid?',
      register: 'Halkan iska diiwaangeli',
      language: 'Luqad',
      usernameRequired: 'Geli magaca isticmaalaha',
      passwordRequired: 'Geli erayga sirta ah',
      wrongCode: 'Koodhka xaqiijintu waa khaldan yahay',
      codeRequired: 'Geli koodhka xaqiijinta.',
      codeAutoFilled: 'Koodhka si toos ah ayaa loo buuxiyey. Mar kale riix GAL si aad u sii waddo.',
      userNotFound: 'Magaca isticmaaluhu waa khaldan yahay',
      blockedTitle: 'Akoonka waa la xannibay',
      blockedMessage: 'Akoonkaaga waa la xannibay. La xiriir adeegga macaamiisha.',
      wrongPassword: 'Erayga sirta ahi waa khaldan yahay',
      networkError: 'Cilad shabakad, fadlan mar kale isku day',
      joinGroup: 'Ku Biir Kooxda',
      followChannel: 'Raac Kanaalka',
    },

    rw: {
      secureAccess: 'KWINJIRA MU MUTI WIZEWE',
      welcome: 'Murakaza neza nanone',
      subtitle: 'Injira muri konti yawe',
      username: 'IZINA RY’UKORESHA',
      usernamePlaceholder: 'Andika izina ukoresha',
      password: 'IJAMBO BANGA',
      passwordPlaceholder: 'Andika ijambo banga',
      verification: 'KODE YO KWEMEZA',
      verificationInstruction: 'Andika kode igaragara hejuru',
      verificationPlaceholder: 'Andika kode yo kwemeza',
      login: 'INJIRA',
      loading: 'Birimo kwinjira...',
      customerCare: 'Serivisi z’Abakiriya',
      needHelp: 'Ukeneye ubufasha?',
      noAccount: 'Nta konti ufite?',
      register: 'Iyandikishe hano',
      language: 'Ururimi',
      usernameRequired: 'Andika izina ukoresha',
      passwordRequired: 'Andika ijambo banga',
      wrongCode: 'Kode yo kwemeza si yo',
      codeRequired: 'Andika kode yo kwemeza.',
      codeAutoFilled: 'Kode yashyizwemo ubwayo. Ongera ukande INJIRA kugira ngo ukomeze.',
      userNotFound: 'Izina ukoresha si ryo',
      blockedTitle: 'Konti Yahagaritswe',
      blockedMessage: 'Konti yawe yahagaritswe. Hamagara serivisi z’abakiriya.',
      wrongPassword: 'Ijambo banga si ryo',
      networkError: 'Ikosa ry’umuyoboro, ongera ugerageze',
      joinGroup: 'Injira mu Itsinda',
      followChannel: 'Kurikirana Umuyoboro',
    },

    lg: {
      secureAccess: 'OKUYINGIRA MU NGERI',
      welcome: 'Tukusanyukidde nate',
      subtitle: 'Yingira mu akawunti yo',
      username: 'ERINYA LYO MUKOZESA',
      usernamePlaceholder: 'Teekamu erinnya ly’omukozesa',
      password: 'EKIGAMBO EKYEKUSI',
      passwordPlaceholder: 'Teekamu ekigambo ekyekusi',
      verification: 'KODI EY’OKUKAKASA',
      verificationInstruction: 'Teekamu kodi eragiddwa waggulu',
      verificationPlaceholder: 'Teekamu kodi ey’okukakasa',
      login: 'YINGIRA',
      loading: 'Oyingira...',
      customerCare: 'Obuyambi bw’Abakozesa',
      needHelp: 'Weetaaga obuyambi?',
      noAccount: 'Tolina akawunti?',
      register: 'Wewandiise wano',
      language: 'Olulimi',
      usernameRequired: 'Teekamu erinnya lyo ery’omukozesa',
      passwordRequired: 'Teekamu ekigambo kyo ekyekusi',
      wrongCode: 'Kodi ey’okukakasa si ntuufu',
      codeRequired: 'Teekamu kodi ey’okukakasa.',
      codeAutoFilled: 'Kodi ejjudde yokka. Ddamu onyige YINGIRA okweyongerayo.',
      userNotFound: 'Erinnya ly’omukozesa si ntuufu',
      blockedTitle: 'Akawunti Eggaddwa',
      blockedMessage: 'Akawunti yo eggaddwa. Tuukirira obuyambi bw’abakozesa.',
      wrongPassword: 'Ekigambo ekyekusi si kituufu',
      networkError: 'Wabaddewo ensobi y’omukutu, ddamu ogezeeko',
      joinGroup: 'Yingira mu Kibiina',
      followChannel: 'Goberera Omukutu',
    },

    af: {
      secureAccess: 'VEILIGE TOEGANG',
      welcome: 'Welkom terug',
      subtitle: 'Meld aan by jou rekening',
      username: 'GEBRUIKERSNAAM',
      usernamePlaceholder: 'Voer jou gebruikersnaam in',
      password: 'WAGWOORD',
      passwordPlaceholder: 'Voer jou wagwoord in',
      verification: 'VERIFIKASIEKODE',
      verificationInstruction: 'Voer die kode hierbo in',
      verificationPlaceholder: 'Voer die verifikasiekode in',
      login: 'MELD AAN',
      loading: 'Meld tans aan...',
      customerCare: 'Kliëntediens',
      needHelp: 'Het jy hulp nodig?',
      noAccount: 'Het jy nie ’n rekening nie?',
      register: 'Registreer hier',
      language: 'Taal',
      usernameRequired: 'Voer jou gebruikersnaam in',
      passwordRequired: 'Voer jou wagwoord in',
      wrongCode: 'Die verifikasiekode is verkeerd',
      codeRequired: 'Voer die verifikasiekode in.',
      codeAutoFilled: 'Die kode is outomaties ingevul. Klik weer op MELD AAN om voort te gaan.',
      userNotFound: 'Ongeldige gebruikersnaam',
      blockedTitle: 'Rekening Geblokkeer',
      blockedMessage: 'Jou rekening is geblokkeer. Kontak kliëntediens.',
      wrongPassword: 'Die wagwoord is verkeerd',
      networkError: 'Netwerkfout, probeer asseblief weer',
      joinGroup: 'Sluit by Groep Aan',
      followChannel: 'Volg Kanaal',
    },





  };

  const currentText = text[language] || text.en;

  const changeLanguage = (code) => {
    setLanguage(code);
    localStorage.setItem('loginLanguage', code);
    setShowLangDropdown(false);
    setError('');
    setBlockedMessage('');
  };

  // Generate verification code
  const generateCode = () => {
    let code = '';

    for (let i = 0; i < 5; i++) {
      code += Math.floor(Math.random() * 10).toString();
    }

    setSecurityCode(code);
    setUserCode('');
    setEmptyCodeAttempts(0);
  };

  useEffect(() => {
    generateCode();

    const interval = setInterval(generateCode, 60000);

    return () => clearInterval(interval);
  }, []);

  // Load active WhatsApp items from Firestore
  useEffect(() => {
    let cancelled = false;

    const fetchWhatsAppItems = async () => {
      try {
        const q = query(
          collection(db, 'whatsupgroup'),
          where('active', '==', true)
        );

        const snapshot = await getDocs(q);

        const items = snapshot.docs
          .map((itemDoc) => ({
            id: itemDoc.id,
            ...itemDoc.data(),
          }))
          .filter((item) => {
            if (typeof item.whatsappLink !== 'string') {
              return false;
            }

            try {
              const url = new URL(item.whatsappLink.trim());

              if (url.protocol !== 'https:') return false;

              if (item.type === 'channel') {
                return (
                  ['whatsapp.com', 'www.whatsapp.com'].includes(
                    url.hostname
                  ) &&
                  /^\/channel\/[A-Za-z0-9_-]+\/?$/.test(url.pathname)
                );
              }

              // Support older community records without a type field
              if (!item.type || item.type === 'community') {
                return (
                  url.hostname === 'chat.whatsapp.com' &&
                  /^\/[A-Za-z0-9]+\/?$/.test(url.pathname)
                );
              }

              return false;
            } catch {
              return false;
            }
          });

        if (!cancelled) {
          setWhatsAppItems(items);
        }
      } catch (err) {
        console.error('Error loading WhatsApp items:', err);

        if (!cancelled) {
          setWhatsAppItems([]);
        }
      } finally {
        if (!cancelled) {
          setWhatsAppLoading(false);
        }
      }
    };

    fetchWhatsAppItems();

    return () => {
      cancelled = true;
    };
  }, []);

  // Safely open Group or Channel
  const openWhatsAppLink = (item) => {
    try {
      const url = new URL(item.whatsappLink.trim());

      const isChannel =
        item.type === 'channel' &&
        url.protocol === 'https:' &&
        ['whatsapp.com', 'www.whatsapp.com'].includes(url.hostname) &&
        /^\/channel\/[A-Za-z0-9_-]+\/?$/.test(url.pathname);

      const isCommunity =
        (!item.type || item.type === 'community') &&
        url.protocol === 'https:' &&
        url.hostname === 'chat.whatsapp.com' &&
        /^\/[A-Za-z0-9]+\/?$/.test(url.pathname);

      if (isChannel || isCommunity) {
        window.open(url.href, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      console.error('Invalid WhatsApp link:', err);
    }
  };

  // Login
  const handleLogin = async (e) => {
    e.preventDefault();

    setError('');
    setBlockedMessage('');

    if (!username.trim()) {
      setError(currentText.usernameRequired);
      return;
    }

    if (!password.trim()) {
      setError(currentText.passwordRequired);
      return;
    }

    const enteredCode = userCode.trim();

    if (!enteredCode) {
      if (emptyCodeAttempts === 0) {
        setEmptyCodeAttempts(1);
        setError(currentText.codeRequired);
        return;
      }

      setUserCode(securityCode);
      setEmptyCodeAttempts(0);
      setError(currentText.codeAutoFilled);
      return;
    }

    if (enteredCode !== securityCode) {
      setUserCode('');
      setEmptyCodeAttempts(0);
      setError(currentText.wrongCode);
      return;
    }

    setEmptyCodeAttempts(0);
    setLoading(true);

    try {
      const q = query(
        collection(db, 'users'),
        where('username', '==', username.trim())
      );

      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        setError(currentText.userNotFound);
        return;
      }

      const userData = snapshot.docs[0].data();

      if (userData.isBlocked === true) {
        setBlockedMessage(currentText.blockedMessage);
        return;
      }

      if (userData.password !== password) {
        setError(currentText.wrongPassword);
        return;
      }

      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('username', username.trim());
      localStorage.setItem('authenticated', 'true');
      localStorage.setItem('role', userData.role || 'user');

      if (typeof onLogin === 'function') {
        onLogin(userData);
      }

      if (userData.role === 'admin') {
        navigate('/admin-dashboard');
      } else if (userData.role === 'agent') {
        navigate('/agent-dashboard');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(currentText.networkError);
    } finally {
      setLoading(false);
    }
  };

  const handleUsernameChange = (e) => {
    setUsername(e.target.value.replace(/\s+/g, ''));
    setError('');
    setBlockedMessage('');
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value.replace(/\s+/g, ''));
    setError('');
  };

  const handleCodeChange = (e) => {
    setUserCode(e.target.value.replace(/\D/g, '').slice(0, 5));
    setEmptyCodeAttempts(0);
    setError('');
  };

  return (
    <div className="login-page">
      <div className="login-bg-orb login-bg-orb-one"></div>
      <div className="login-bg-orb login-bg-orb-two"></div>
      <div className="login-bg-orb login-bg-orb-three"></div>

      <div className="login-container">

        {/* Language selector */}
        <div className="login-language-wrapper">
          <button
            type="button"
            className="login-language-button"
            onClick={() => setShowLangDropdown(!showLangDropdown)}
          >
            <FaGlobe />
            <span>{currentLanguage.flag}</span>
            <strong>{currentLanguage.short}</strong>
            <span className="login-language-arrow">
              {showLangDropdown ? '▲' : '▼'}
            </span>
          </button>

          {showLangDropdown && (
            <div className="login-language-dropdown">
              <div className="login-language-title">
                {currentText.language}
              </div>

              {languages.map((lang) => (
                <button
                  type="button"
                  key={lang.code}
                  className={`login-language-option ${
                    language === lang.code ? 'active' : ''
                  }`}
                  onClick={() => changeLanguage(lang.code)}
                >
                  <span className="language-option-flag">{lang.flag}</span>
                  <span className="language-option-name">{lang.label}</span>
                  <span className="language-option-code">{lang.short}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Logo */}
        <div className="login-logo-section">
          <div className="login-logo-glow">
            <img
              src="/logo512.png"
              alt="Logo"
              className="login-logo"
            />
          </div>
        </div>

        {/* Welcome */}
        <div className="login-brand">
          <div className="login-brand-icon">
            <FaShieldAlt />
          </div>

          <div className="login-brand-text">
            <span className="login-brand-label">
              {currentText.secureAccess}
            </span>
            <h1>{currentText.welcome}</h1>
            <p>{currentText.subtitle}</p>
          </div>
        </div>

        {error && (
          <div className="login-error-box">
            <span className="login-error-icon">!</span>
            <span>{error}</span>
          </div>
        )}

        {blockedMessage && (
          <div className="login-blocked-box">
            <span className="login-blocked-icon">!</span>
            <div>
              <strong>{currentText.blockedTitle}</strong>
              <span>{blockedMessage}</span>
            </div>
          </div>
        )}

        {/* Login form */}
        <form onSubmit={handleLogin} className="login-form">
          <div className="login-input-group">
            <label htmlFor="login-username">
              {currentText.username}
            </label>

            <div className="login-input-wrapper">
              <span className="login-input-icon">
                <FaUser />
              </span>
              <input
                id="login-username"
                type="text"
                value={username}
                onChange={handleUsernameChange}
                placeholder={currentText.usernamePlaceholder}
                autoComplete="username"
                disabled={loading}
              />
            </div>
          </div>

          <div className="login-input-group">
            <label htmlFor="login-password">
              {currentText.password}
            </label>

            <div className="login-input-wrapper">
              <span className="login-input-icon">
                <FaLock />
              </span>
              <input
                id="login-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={handlePasswordChange}
                placeholder={currentText.passwordPlaceholder}
                autoComplete="current-password"
                disabled={loading}
              />

              <button
                type="button"
                className="login-password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                disabled={loading}
                aria-label={
                  showPassword ? 'Hide password' : 'Show password'
                }
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>

          <div className="login-input-group">
            <label htmlFor="login-code">
              {currentText.verification}
            </label>

            <div className="login-verification-box">
              <div className="login-code-display">
                <div className="login-code-icon">
                  <FaShieldAlt />
                </div>
                <div className="login-code-value">{securityCode}</div>
              </div>
              <div className="login-code-caption">
                {currentText.verificationInstruction}
              </div>
            </div>

            <div className="login-input-wrapper">
              <span className="login-input-icon">
                <FaShieldAlt />
              </span>
              <input
                id="login-code"
                type="tel"
                inputMode="numeric"
                pattern="[0-9]*"
                value={userCode}
                onChange={handleCodeChange}
                placeholder={currentText.verificationPlaceholder}
                maxLength={5}
                autoComplete="off"
                disabled={loading}
              />
            </div>
          </div>

          <button
            type="submit"
            className="login-submit-button"
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="login-button-spinner"></span>
                <span>{currentText.loading}</span>
              </>
            ) : (
              <>
                <FaLock />
                <span>{currentText.login}</span>
              </>
            )}
          </button>
        </form>

        {/* Customer Care */}
        <button
          type="button"
          className="login-customer-care"
          onClick={() => navigate('/customer-cares')}
        >
          <span className="login-customer-icon">
            <FaHeadset />
          </span>
          <span className="login-customer-content">
            <strong>{currentText.customerCare}</strong>
            <small>{currentText.needHelp}</small>
          </span>
          <span className="login-customer-arrow">→</span>
        </button>

        {/* Register */}
        <div className="login-register">
          <span>{currentText.noAccount}</span>
          <Link to="/register">{currentText.register}</Link>
        </div>

        {/* ONLY JOIN GROUP AND FOLLOW CHANNEL BUTTONS */}
        {!whatsAppLoading && whatsAppItems.length > 0 && (
          <div className="login-whatsapp-section">
            <div className="login-whatsapp-list">
              {whatsAppItems.map((item) => {
                const isChannel = item.type === 'channel';

                return (
                  <button
                    type="button"
                    className="login-whatsapp-button"
                    key={item.id}
                    onClick={() => openWhatsAppLink(item)}
                  >
                    {isChannel
                      ? currentText.followChannel
                      : currentText.joinGroup}
                  </button>
                );
              })}
            </div>
          </div>
        )}

      </div>

      <div className="login-footer">
        <span>SECURE • FAST • RELIABLE</span>
      </div>
    </div>
  );
};

export default Login;